import { JobStatusNewType, JobType, type JobPriorityType } from '@/enum'
import type {
  IDocument,
  IJobAssociatedAssetInfo,
  IJobAssociatedAssetUpdate,
  IJobAssociatedMaintenanceType,
  IJobAssociatedTaskTemplate,
  IJobForm,
  IJobFormScheduleOptions,
  IVendor
} from '@/interfaces'
import type { IAppUser } from '@/interfaces/app-user/IAppUser'
import { isDefined, toLocalDateTimeFromUtc } from '@/utils/helpers'
import AppUser from '../AppUser'
import DocumentModel from '../DocumentModel'
import JobAssociatedTaskTemplate from './JobAssociatedTaskTemplate'
import JobFormScheduleOptions from './JobFormScheduleOptions'
import DecoratorBase from '../DecoratorBase'

class JobForm extends DecoratorBase implements IJobForm {
  title: string
  description?: string
  type?: JobType
  status?: JobStatusNewType
  priority?: JobPriorityType | null
  asset?: IJobAssociatedAssetUpdate
  assetInfo?: IJobAssociatedAssetInfo
  assignedEmployeeIds?: string[]
  employees?: IAppUser[] | null
  issueReporterId?: string
  issueReporter?: IAppUser
  maintenanceTypes?: IJobAssociatedMaintenanceType[]
  workOrderVendor?: IVendor
  workOrderUrl?: string
  taskTemplates: IJobAssociatedTaskTemplate[]
  documents?: IDocument[]
  enforceSequentialTaskCompletion?: boolean
  scheduledAt?: string | null
  dueDate?: string | null
  closedAt?: string | null
  startedAt?: string | null
  number?: string | null
  isOdometerBasedSelected?: boolean
  isMachineHoursBasedSelected?: boolean
  isTimeBasedSelected?: boolean
  options: IJobFormScheduleOptions
  workOrderVendorId?: string
  affectCompletedTasks?: boolean
  targetOdometerReading?: number | null
  targetMachineHours?: number | null

  constructor(jobForm?: any) {
    super(jobForm)
    this.title = jobForm?.title ?? ''
    this.number = jobForm?.number ?? null
    this.description = jobForm?.description
    this.type = jobForm?.type
    this.status = jobForm?.status
    this.priority = jobForm?.priority
    this.scheduledAt = jobForm?.scheduledAt
    this.dueDate = jobForm?.dueDate
    this.asset = jobForm?.asset
    this.assetInfo = jobForm?.assetInfo
    this.assignedEmployeeIds = jobForm?.employees
      ? jobForm.employees.map((assignedEmployee: any) => assignedEmployee.employeeId)
      : []
    this.employees = jobForm?.employees
      ? jobForm?.employees.map((assignedEmployee: any) => {
          return {
            ...new AppUser(assignedEmployee.employee),
            order: assignedEmployee.order
          }
        })
      : null
    this.workOrderVendor = jobForm?.workOrderVendor
    this.workOrderUrl = jobForm?.workOrderUrl
    this.taskTemplates =
      jobForm?.taskTemplates?.map(
        (taskTemplate: any) => new JobAssociatedTaskTemplate(taskTemplate)
      ) ?? []
    this.documents = jobForm?.documents?.map((document: any) => new DocumentModel(document)) ?? []
    this.enforceSequentialTaskCompletion = jobForm?.enforceSequentialTaskCompletion ?? true
    this.closedAt = jobForm?.closedAt ? toLocalDateTimeFromUtc(jobForm?.closedAt) : null
    this.scheduledAt = jobForm?.scheduledAt ? toLocalDateTimeFromUtc(jobForm?.scheduledAt) : null
    this.startedAt = jobForm?.startedAt ? toLocalDateTimeFromUtc(jobForm?.startedAt) : null
    this.options = new JobFormScheduleOptions(jobForm?.options)
    this.targetMachineHours = jobForm?.targetMachineHours ?? null
    this.targetOdometerReading = jobForm?.targetOdometerReading ?? null
    this.isOdometerBasedSelected = !!(
      this.options?.odometerTrigger?.value || this.targetOdometerReading
    )
    this.isMachineHoursBasedSelected = !!(
      this.options?.machineHoursTrigger?.value || this.targetMachineHours
    )
    this.isTimeBasedSelected = this.options?.timeBasedTrigger?.pattern != null
    this.workOrderVendorId = jobForm?.workOrderVendorId
    this.affectCompletedTasks = jobForm?.affectCompletedTasks
  }

  isRecurring() {
    return (
      isDefined(this.options.timeBasedTrigger.pattern) ||
      !!this.options.odometerTrigger.value ||
      !!this.options.machineHoursTrigger.value
    )
  }
}

export default JobForm
